/**
 * The ledger wire client (P4-T2).
 *
 * It speaks exactly the surface P4-T1 merged — one route per operation, the `{data, meta}`
 * envelope, a strong `If-Match` on edits, a UUID `Idempotency-Key` on every financial
 * write, and the JSON error envelope — and consumes it without re-deciding any of it:
 *
 *   GET    /api/v1/accounts          list, with each account's derived balance
 *   POST   /api/v1/accounts          create, optionally with its additive opening
 *   GET    /api/v1/accounts/:id      one account
 *   PATCH  /api/v1/accounts/:id      rename (`If-Match` required)
 *   GET    /api/v1/entries           one account's rows, in derived-date order
 *   POST   /api/v1/entries           one ordinary cash income or expense
 *   GET    /api/v1/entries/:id       one entry
 *   PATCH  /api/v1/entries/:id       edit, **and the cash pay** (`If-Match` required)
 *
 * and the installment group surface (P5-T2), which is always the pair — a preview, then the
 * commit of that stored preview:
 *
 *   GET    /api/v1/installment-groups/:id   one group and its members
 *   POST   /api/v1/mutation-previews        preview an installment create/edit/delete
 *   POST   /api/v1/mutations                commit that stored preview (`If-Match` on the group)
 *
 * Responses are validated with `@saldo/contracts`' precompiled validators, so a wire change
 * fails here with a named contract error instead of being rendered, and nothing on this
 * path compiles a schema (no `'unsafe-eval'`). Every failure is classified rather than
 * surfaced raw: the caller decides what a `412` means to the editor, and the server's own
 * text is never echoed into the page.
 */

import {
  parseErrorEnvelope,
  parseLedgerAccountEnvelope,
  parseLedgerAccountsEnvelope,
  parseLedgerBulkPreviewEnvelope,
  parseLedgerBulkResultEnvelope,
  parseLedgerCategoriesEnvelope,
  parseLedgerCategoryEnvelope,
  parseLedgerEntriesEnvelope,
  parseLedgerEntryEnvelope,
  parseLedgerEntryLifecyclePreviewEnvelope,
  parseLedgerEntrySuggestionsEnvelope,
  parseLedgerInstallmentGroupEnvelope,
  parseLedgerMutationPreviewEnvelope,
  parseLedgerRecurrenceEnvelope,
  parseLedgerRecurrencePreviewEnvelope,
  parseLedgerRecurrenceTemplateEnvelope,
  parseLedgerRecurrencesEnvelope,
  parseLedgerTransferEnvelope,
  parseLedgerTransferPairSuggestionsEnvelope,
  parseLedgerTransferPreviewEnvelope,
  parseLedgerTransfersEnvelope,
  type AccountBody,
  type AccountCreateRequest,
  type AccountPatchRequest,
  type BulkCommitResultBody,
  type BulkMutationPreviewRequest,
  type BulkPreviewBody,
  type CategoryBody,
  type CategoryCreateRequest,
  type CategoryPatchRequest,
  type EntryBody,
  type EntryCreateRequest,
  type EntryLifecycleMutationPreviewRequest,
  type EntryLifecyclePreviewBody,
  type EntryPatchRequest,
  type EntrySuggestionBody,
  type FieldError,
  type InstallmentGroupBody,
  type LedgerAccountEnvelope,
  type LedgerAccountsEnvelope,
  type LedgerBulkPreviewEnvelope,
  type LedgerBulkResultEnvelope,
  type LedgerCategoriesEnvelope,
  type LedgerCategoryEnvelope,
  type LedgerEntriesEnvelope,
  type LedgerEntryEnvelope,
  type LedgerEntryLifecyclePreviewEnvelope,
  type LedgerEntrySuggestionsEnvelope,
  type LedgerInstallmentGroupEnvelope,
  type LedgerMeta,
  type LedgerMutationPreviewEnvelope,
  type LedgerRecurrenceEnvelope,
  type LedgerRecurrencePreviewEnvelope,
  type LedgerRecurrenceTemplateEnvelope,
  type LedgerRecurrencesEnvelope,
  type LedgerTransferEnvelope,
  type LedgerTransferPreviewEnvelope,
  type LedgerTransferPairSuggestionsEnvelope,
  type LedgerTransfersEnvelope,
  type TransferPairSuggestionBody,
  type MutationCommitRequest,
  type MutationPreviewBody,
  type MutationPreviewRequest,
  type RecurrenceDetailBody,
  type RecurrenceMutationPreviewRequest,
  type RecurrencePreviewBody,
  type RecurrenceTemplateBody,
  type TransferBody,
  type TransferMutationPreviewRequest,
  type TransferPreviewBody,
} from '@saldo/contracts';

export const ACCOUNTS_PATH = '/api/v1/accounts';
export const CATEGORIES_PATH = '/api/v1/categories';
export const ENTRIES_PATH = '/api/v1/entries';
export const SUGGESTIONS_PATH = '/api/v1/entry-suggestions';
export const GROUPS_PATH = '/api/v1/installment-groups';
export const PREVIEWS_PATH = '/api/v1/mutation-previews';
export const MUTATIONS_PATH = '/api/v1/mutations';
export const TRANSFERS_PATH = '/api/v1/transfers';
/**
 * The recurrence surface (P8-T2): the schedules read, one schedule with its classified
 * occurrences, and the same preview/commit pair every review-required action uses. There is no
 * route that generates: generation is the host's one-shot entry point, never a request.
 */
export const RECURRENCES_PATH = '/api/v1/recurrences';
export const CSRF_HEADER = 'X-CSRF-Token';

/** What a failed ledger request means to the surface that made it. */
export type LedgerFailureKind =
  | 'unauthenticated'
  | 'forbidden'
  | 'not-found'
  | 'revision-required'
  | 'revision-stale'
  | 'intent-conflict'
  | 'rejected'
  | 'throttled'
  | 'contract'
  | 'unavailable';

/**
 * Status to kind, plus the envelope's own code for the two cases the status alone cannot
 * separate: `409` is an intent conflict and `428`/`412` are the two revision refusals.
 */
export function classifyLedgerFailure(status: number, code: string | undefined): LedgerFailureKind {
  if (status === 0) return 'unavailable';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 429) return 'throttled';
  if (status === 409) return code === 'INTENT_CHANGED' ? 'intent-conflict' : 'rejected';
  if (status === 428) return 'revision-required';
  if (status === 412) return 'revision-stale';
  if (status >= 500) return 'unavailable';
  return 'rejected';
}

/**
 * Only a `401` ends the session.
 *
 * A refused *request* — a stale revision, a changed intent, a rejected body — is not a
 * refused *session*, and signing the owner out on one would turn a correctable mistake into
 * lost work. The same rule the session boundary applies (`session/client.ts`).
 */
export function endsSession(kind: LedgerFailureKind): boolean {
  return kind === 'unauthenticated';
}

export class LedgerRequestError extends Error {
  override readonly name = 'LedgerRequestError';
  readonly kind: LedgerFailureKind;
  /** `null` when the request never got an answer. */
  readonly status: number | null;
  /** The envelope's own safe code, for the log line. Never rendered. */
  readonly code: string | undefined;
  /**
   * The envelope's own `fieldErrors`, kept rather than dropped (P7-T1).
   *
   * A *named* refusal is carried here and nowhere else: the reference-safe deletion answers `409`
   * with `fieldErrors: [{path: '/references', code: 'entry'}]`, and that list is what lets the
   * surface say *which* reference blocked the deletion instead of a generic refusal. The paths and
   * codes are the application's own vocabulary, never server text, so keeping them is safe.
   */
  readonly fieldErrors: readonly FieldError[];

  constructor(
    kind: LedgerFailureKind,
    status: number | null,
    code?: string,
    fieldErrors: readonly FieldError[] = [],
  ) {
    super(`ledger request failed (${kind})`);
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }

  static unreachable(): LedgerRequestError {
    return new LedgerRequestError('unavailable', null);
  }
}

/** One generic line per kind: no server text is echoed, and no field is named. */
export function describeLedgerFailure(kind: LedgerFailureKind): string {
  switch (kind) {
    case 'unauthenticated':
      return 'Your session ended. Sign in again to continue.';
    case 'forbidden':
      return 'The request was refused. Reload the page and try again.';
    case 'not-found':
      return 'That account or entry is no longer available.';
    case 'revision-required':
      return 'This entry changed since the editor opened. Reload it before saving.';
    case 'revision-stale':
      return 'This entry was changed elsewhere. Reload it to see the current version.';
    case 'intent-conflict':
      return 'That operation key was already used for a different change. Start a new save.';
    case 'rejected':
      return 'The change was refused. Check the fields and try again.';
    case 'throttled':
      return 'Too many requests. Wait a moment and try again.';
    case 'contract':
      return 'The server answered something this application does not understand.';
    case 'unavailable':
      return 'The server could not be reached. Try again.';
  }
}

async function failureOf(response: Response): Promise<LedgerRequestError> {
  let code: string | undefined;
  let fieldErrors: readonly FieldError[] = [];
  try {
    const envelope = parseErrorEnvelope(await response.json()).error;
    code = envelope.code;
    fieldErrors = envelope.fieldErrors;
  } catch {
    // Not the application's envelope: the status is still the answer.
  }
  return new LedgerRequestError(
    classifyLedgerFailure(response.status, code),
    response.status,
    code,
    fieldErrors,
  );
}

interface RequestInit_ {
  readonly method: string;
  readonly csrfToken?: string | undefined;
  readonly headers?: Record<string, string>;
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
}

async function send(path: string, init: RequestInit_): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json', ...init.headers };
  if (init.csrfToken !== undefined) headers[CSRF_HEADER] = init.csrfToken;
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  try {
    return await fetch(path, {
      method: init.method,
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      // Same-origin: the session cookie is HttpOnly and scoped to this origin, stated
      // rather than inherited so a later base cannot silently change it.
      credentials: 'same-origin',
      signal: init.signal,
    });
  } catch (error) {
    if (init.signal?.aborted === true) throw error;
    throw LedgerRequestError.unreachable();
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new LedgerRequestError('contract', response.status);
  }
}

function parseSafely<T>(parse: (value: unknown) => T, value: unknown, status: number): T {
  try {
    return parse(value);
  } catch {
    throw new LedgerRequestError('contract', status);
  }
}

/** A write carries the CSRF token and its idempotency key; an edit also carries `If-Match`. */
export interface WriteHeaders {
  readonly idempotencyKey: string;
  readonly ifMatch?: string;
}

function writeHeaders(write: WriteHeaders): Record<string, string> {
  const headers: Record<string, string> = { 'idempotency-key': write.idempotencyKey };
  if (write.ifMatch !== undefined) headers['if-match'] = `"${write.ifMatch}"`;
  return headers;
}

export interface AccountsRead {
  readonly accounts: AccountBody[];
  readonly meta: LedgerMeta;
}

export async function listAccounts(csrfToken: string | undefined, signal?: AbortSignal): Promise<AccountsRead> {
  const response = await send(ACCOUNTS_PATH, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerAccountsEnvelope>(
    parseLedgerAccountsEnvelope,
    await readJson(response),
    response.status,
  );
  return { accounts: body.data.accounts, meta: body.meta };
}

export async function createAccount(
  csrfToken: string | undefined,
  body: AccountCreateRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<AccountBody> {
  const response = await send(ACCOUNTS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerAccountEnvelope>(parseLedgerAccountEnvelope, await readJson(response), response.status)
    .data;
}

/**
 * `PATCH /api/v1/accounts/{id}` — the account metadata patch.
 *
 * One call serves both facts the patch can carry: a rename and the reversible hide/unhide
 * (`archived`), because they are one request shape at the API and one `If-Match`ed write.
 */
export async function patchAccount(
  csrfToken: string | undefined,
  id: string,
  body: AccountPatchRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<AccountBody> {
  const response = await send(`${ACCOUNTS_PATH}/${id}`, {
    method: 'PATCH',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerAccountEnvelope>(parseLedgerAccountEnvelope, await readJson(response), response.status)
    .data;
}

export interface EntriesRead {
  readonly entries: EntryBody[];
  readonly meta: LedgerMeta;
}

export async function listEntries(
  csrfToken: string | undefined,
  accountId: string,
  options: {
    readonly limit?: number;
    readonly type?: string;
    readonly includeCanceled?: boolean;
    readonly signal?: AbortSignal;
  } = {},
): Promise<EntriesRead> {
  const params = new URLSearchParams({ accountId });
  params.set('limit', String(options.limit ?? 200));
  if (options.type !== undefined) params.set('type', options.type);
  // Sent only when it is `true`, as the wire's own string: the flag widens the read to include
  // canceled rows, so its absence is the ordinary live-ledger request. An absent flag and an
  // explicit `false` are the same request — `entryListQuerySchema` makes it optional and the read
  // matches `'true'` alone — so nothing is lost by leaving it out of an ordinary read.
  if (options.includeCanceled === true) params.set('includeCanceled', 'true');
  const response = await send(`${ENTRIES_PATH}?${params.toString()}`, {
    method: 'GET',
    csrfToken,
    signal: options.signal,
  });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerEntriesEnvelope>(
    parseLedgerEntriesEnvelope,
    await readJson(response),
    response.status,
  );
  return { entries: body.data.entries, meta: body.meta };
}

export async function readEntry(
  csrfToken: string | undefined,
  id: string,
  signal?: AbortSignal,
): Promise<EntryBody> {
  const response = await send(`${ENTRIES_PATH}/${id}`, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryEnvelope>(parseLedgerEntryEnvelope, await readJson(response), response.status).data;
}

export async function createEntry(
  csrfToken: string | undefined,
  body: EntryCreateRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<EntryBody> {
  const response = await send(ENTRIES_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryEnvelope>(parseLedgerEntryEnvelope, await readJson(response), response.status).data;
}

export async function updateEntry(
  csrfToken: string | undefined,
  id: string,
  body: EntryPatchRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<EntryBody> {
  const response = await send(`${ENTRIES_PATH}/${id}`, {
    method: 'PATCH',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryEnvelope>(parseLedgerEntryEnvelope, await readJson(response), response.status).data;
}

/**
 * The installment group surface (P5-T2).
 *
 * `POST /mutations` returns the group as it now stands, which is why the commit and the group
 * read share one parser: what a commit answers is the same resource a group read answers.
 */

export async function readInstallmentGroup(
  csrfToken: string | undefined,
  groupId: string,
  signal?: AbortSignal,
): Promise<InstallmentGroupBody> {
  const response = await send(`${GROUPS_PATH}/${groupId}`, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerInstallmentGroupEnvelope>(
    parseLedgerInstallmentGroupEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Store a preview of one installment change.
 *
 * It is a financial write in its own right — it carries its `Idempotency-Key`, so an
 * unknown-outcome retry of the *same* preview replays the stored one instead of minting a
 * second. The preview is what the commit then names, so the eligibility decided here is the
 * eligibility committed.
 */
export async function createMutationPreview(
  csrfToken: string | undefined,
  body: MutationPreviewRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<MutationPreviewBody> {
  const response = await send(PREVIEWS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerMutationPreviewEnvelope>(
    parseLedgerMutationPreviewEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Commit a stored preview, confirmed.
 *
 * `If-Match` is the group's revision, and it is sent only when there is a group to version: a
 * creation has none, an edit or a deletion must state the revision it read. The service is the
 * layer that enforces that (`428`/`412`), which is why the header rides the `write` and is
 * optional here rather than required by the client.
 */
export async function commitMutation(
  csrfToken: string | undefined,
  previewId: string,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<InstallmentGroupBody> {
  const body: MutationCommitRequest = { previewId, confirm: true };
  const response = await send(MUTATIONS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerInstallmentGroupEnvelope>(
    parseLedgerInstallmentGroupEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/* ---------------------------------------------------- explicit-ID bulk (P8-T3) --- */

/**
 * Store one bulk preview, from an exact id list.
 *
 * The body arrives already built by `apps/web/src/ledger/bulk.ts`, whose only inputs are the
 * selection and the action's own one value — there is no branch here that could widen the set. The
 * answer is the bulk preview rather than the installment one, so it is parsed as its own envelope:
 * a bulk action names many rows and has no group to locate.
 */
export async function createBulkPreview(
  csrfToken: string | undefined,
  body: BulkMutationPreviewRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<BulkPreviewBody> {
  const response = await send(PREVIEWS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerBulkPreviewEnvelope>(
    parseLedgerBulkPreviewEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Commit a stored bulk preview, confirmed.
 *
 * No `If-Match`: a bulk is many rows and the preview's own stored selection *is* the version that
 * matters, so the API rechecks every row against what the preview recorded and answers
 * `409 STALE_PREVIEW` when any of them moved (`BAL-07`, `SAFE-02`).
 */
export async function commitBulkMutation(
  csrfToken: string | undefined,
  previewId: string,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<BulkCommitResultBody> {
  const body: MutationCommitRequest = { previewId, confirm: true };
  const response = await send(MUTATIONS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerBulkResultEnvelope>(
    parseLedgerBulkResultEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/* -------------------------------------------------------------- payments (P6-T2) --- */

/**
 * The payment surface: an account's transfer pairs, a stored `transfer.*` preview, and the
 * commit of that stored preview.
 *
 * A `transfer.*` preview is **not** the installment preview shape even though it arrives on the
 * same route and in the same envelope, so it is parsed by its own validator and typed as
 * `TransferPreviewBody`: a payment preview can never be rendered as an installment one.
 */

export interface TransfersRead {
  readonly transfers: TransferBody[];
  readonly meta: LedgerMeta;
}

/** `GET /api/v1/transfers` — one owner's pairs, or one account's. */
export async function listTransfers(
  csrfToken: string | undefined,
  options: { readonly accountId?: string; readonly limit?: number; readonly signal?: AbortSignal } = {},
): Promise<TransfersRead> {
  const params = new URLSearchParams();
  if (options.accountId !== undefined) params.set('accountId', options.accountId);
  params.set('limit', String(options.limit ?? 100));
  const response = await send(`${TRANSFERS_PATH}?${params.toString()}`, {
    method: 'GET',
    csrfToken,
    signal: options.signal,
  });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerTransfersEnvelope>(
    parseLedgerTransfersEnvelope,
    await readJson(response),
    response.status,
  );
  return { transfers: body.data.transfers, meta: body.meta };
}

/** The pair-suggestion read's path (P7-T3): account pairs the owner has already used. */
export const TRANSFER_PAIR_SUGGESTIONS_PATH = '/api/v1/transfer-pair-suggestions';

export interface PairSuggestionsRead {
  readonly suggestions: TransferPairSuggestionBody[];
  readonly meta: LedgerMeta;
}

/**
 * `GET /api/v1/transfer-pair-suggestions` — the pairs worth offering for **explicit** reuse.
 *
 * The read is not a rate source and the client does not treat it as one: it returns what the
 * caller has already done, and every field it carries is applied only when the user chooses it.
 * Parsing is by the resource's own validator, so a shape the contract did not admit never reaches
 * a form.
 */
export async function listTransferPairSuggestions(
  csrfToken: string | undefined,
  options: { readonly limit?: number; readonly signal?: AbortSignal } = {},
): Promise<PairSuggestionsRead> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 10));
  const response = await send(`${TRANSFER_PAIR_SUGGESTIONS_PATH}?${params.toString()}`, {
    method: 'GET',
    csrfToken,
    signal: options.signal,
  });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerTransferPairSuggestionsEnvelope>(
    parseLedgerTransferPairSuggestionsEnvelope,
    await readJson(response),
    response.status,
  );
  return { suggestions: body.data.suggestions, meta: body.meta };
}

/**
 * Store a preview of one payment change (`transfer.create|update|complete|delete`).
 *
 * It carries its own `Idempotency-Key`, so an unknown-outcome retry of the *same* preview
 * replays the stored one instead of minting a second, and the commit names exactly this stored
 * preview — which is what makes a stale selection uncommittable.
 */
export async function createTransferPreview(
  csrfToken: string | undefined,
  body: TransferMutationPreviewRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<TransferPreviewBody> {
  const response = await send(PREVIEWS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerTransferPreviewEnvelope>(
    parseLedgerTransferPreviewEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Commit a stored payment preview, confirmed.
 *
 * `If-Match` is the pair's revision, and it is sent only when there is a pair to version: a
 * creation has none, an edit, a completion or a deletion must state the revision it read.
 * The service is the layer that enforces that (`428`/`412`).
 */
export async function commitTransferMutation(
  csrfToken: string | undefined,
  previewId: string,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<TransferBody> {
  const body: MutationCommitRequest = { previewId, confirm: true };
  const response = await send(MUTATIONS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerTransferEnvelope>(
    parseLedgerTransferEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/* ------------------------------------------- categories and the entry lifecycle (P7-T1) --- */

/**
 * The category surface, the autocomplete read, and the entry lifecycle (P7-T1).
 *
 * Three of these are the *same* review-required spine the installments and payments use —
 * `POST /mutation-previews`, then `POST /mutations` naming that stored preview — so the impact the
 * commit applies is the impact that was read, and a stale one is refused rather than silently
 * recomputed. The `entry.*` preview is a third shape on that route and is validated by its own
 * parser, exactly as the `transfer.*` preview is.
 *
 * The two direct routes are deliberately direct: the cancellation/restoration is reversible and
 * moves nothing, and a category or account deletion has no impact to review because it either
 * succeeds or is refused by a named reference.
 */

export interface CategoriesRead {
  readonly categories: CategoryBody[];
  readonly meta: LedgerMeta;
}

/** `GET /api/v1/categories` — one owner's categories, archived ones included. */
export async function listCategories(
  csrfToken: string | undefined,
  signal?: AbortSignal,
): Promise<CategoriesRead> {
  const response = await send(CATEGORIES_PATH, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerCategoriesEnvelope>(
    parseLedgerCategoriesEnvelope,
    await readJson(response),
    response.status,
  );
  return { categories: body.data.categories, meta: body.meta };
}

/** `POST /api/v1/categories` — create, active. */
export async function createCategory(
  csrfToken: string | undefined,
  body: CategoryCreateRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<CategoryBody> {
  const response = await send(CATEGORIES_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerCategoryEnvelope>(
    parseLedgerCategoryEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * `PATCH /api/v1/categories/{id}` — rename and/or the reversible archive.
 *
 * One `archived` boolean, so *unhide* is this same call with `false`; there is no second route.
 */
export async function updateCategory(
  csrfToken: string | undefined,
  id: string,
  body: CategoryPatchRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<CategoryBody> {
  const response = await send(`${CATEGORIES_PATH}/${id}`, {
    method: 'PATCH',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerCategoryEnvelope>(
    parseLedgerCategoryEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * `DELETE /api/v1/categories/{id}` — the reference-safe deletion.
 *
 * Nothing is returned but the fact: a category with entries is refused `409` with the reference
 * named, and that refusal arrives as a `LedgerRequestError` carrying its `fieldErrors`. The
 * `If-Match` is not optional — the API answers `428` without it — so the revision is a parameter
 * rather than something a caller can forget.
 */
export async function deleteCategory(
  csrfToken: string | undefined,
  id: string,
  revision: string,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<void> {
  const response = await send(`${CATEGORIES_PATH}/${id}`, {
    method: 'DELETE',
    csrfToken,
    headers: writeHeaders({ ...write, ifMatch: revision }),
    signal,
  });
  if (!response.ok) throw await failureOf(response);
}

/** `DELETE /api/v1/accounts/{id}` — the reference-safe deletion, with the same named refusal. */
export async function deleteAccount(
  csrfToken: string | undefined,
  id: string,
  revision: string,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<void> {
  const response = await send(`${ACCOUNTS_PATH}/${id}`, {
    method: 'DELETE',
    csrfToken,
    headers: writeHeaders({ ...write, ifMatch: revision }),
    signal,
  });
  if (!response.ok) throw await failureOf(response);
}

export interface SuggestionsRead {
  readonly suggestions: EntrySuggestionBody[];
  readonly meta: LedgerMeta;
}

/**
 * `GET /api/v1/entry-suggestions?q=&accountId=&limit=` — autocomplete over the owner's own past
 * descriptions.
 *
 * A read, and nothing else: reusing a suggestion still goes through the ordinary create or the
 * reviewed duplicate. The parameters are omitted rather than sent empty, because the API's query
 * schema refuses an empty `q` as an empty string is not a substring.
 */
export async function listEntrySuggestions(
  csrfToken: string | undefined,
  options: { readonly q?: string; readonly accountId?: string; readonly limit?: number; readonly signal?: AbortSignal } = {},
): Promise<SuggestionsRead> {
  const params = new URLSearchParams();
  if (options.q !== undefined && options.q !== '') params.set('q', options.q);
  if (options.accountId !== undefined && options.accountId !== '') {
    params.set('accountId', options.accountId);
  }
  params.set('limit', String(options.limit ?? SUGGESTION_PAGE_LIMIT));
  const response = await send(`${SUGGESTIONS_PATH}?${params.toString()}`, {
    method: 'GET',
    csrfToken,
    signal: options.signal,
  });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerEntrySuggestionsEnvelope>(
    parseLedgerEntrySuggestionsEnvelope,
    await readJson(response),
    response.status,
  );
  return { suggestions: body.data.suggestions, meta: body.meta };
}

/** The suggestion read's bound; the API's own maximum, so one read is one page. */
export const SUGGESTION_PAGE_LIMIT = 50;

/**
 * Store a preview of one `entry.delete` / `entry.duplicate`.
 *
 * The response is the impact the API computed — the account, the signed amount, how the derived
 * balance moves, whether it is effective and whether the commit needs the acknowledgement. It is
 * what the surface renders, and it is never recomputed here.
 */
export async function createEntryLifecyclePreview(
  csrfToken: string | undefined,
  body: EntryLifecycleMutationPreviewRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<EntryLifecyclePreviewBody> {
  const response = await send(PREVIEWS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryLifecyclePreviewEnvelope>(
    parseLedgerEntryLifecyclePreviewEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Commit a stored lifecycle preview.
 *
 * `impactAcknowledged` is sent deliberately (`false` included): the API answers `409`
 * `IMPACT_CONFIRMATION_REQUIRED` when the stored impact removes a figure that is already
 * effective and the commit does not acknowledge it, and that refusal is the protection rather than
 * an error to hide. What the commit answers is the entry, so this parses the entry envelope.
 */
export async function commitEntryLifecycle(
  csrfToken: string | undefined,
  previewId: string,
  impactAcknowledged: boolean,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<EntryBody> {
  const body: MutationCommitRequest = { previewId, confirm: true, impactAcknowledged };
  const response = await send(MUTATIONS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryEnvelope>(
    parseLedgerEntryEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * `POST /api/v1/entries/{id}/cancellation` and `…/restoration` — the reversible cancellation.
 *
 * Two paths rather than a body flag, so the intent is in the path. Both are `If-Match`ed and
 * idempotency-keyed, and neither deletes anything: the row keeps its identity and stays readable.
 */
export async function setEntryCancellation(
  csrfToken: string | undefined,
  id: string,
  cancel: boolean,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<EntryBody> {
  const response = await send(`${ENTRIES_PATH}/${id}/${cancel ? 'cancellation' : 'restoration'}`, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerEntryEnvelope>(
    parseLedgerEntryEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/* ------------------------------------------------------------- recurrences (P8-T2) --- */

/**
 * The recurrence surface: the owner's schedules, one schedule with its occurrences classified the
 * way a stop would classify them, and the preview/commit pair for the two actions.
 *
 * The classification is the API's. Nothing here decides which occurrence is eligible-forward, which
 * is protected and which is an edited exception — the surface renders what the classification says
 * and hands back the entry ids a reviewer chose. That is what makes "the preview I read" and "the
 * cancellation I commit" one statement rather than two similar ones.
 */

export interface RecurrencesRead {
  readonly recurrences: RecurrenceTemplateBody[];
  readonly meta: LedgerMeta;
}

/** `GET /api/v1/recurrences` — the owner's schedules, active and stopped alike. */
export async function listRecurrences(
  csrfToken: string | undefined,
  signal?: AbortSignal,
): Promise<RecurrencesRead> {
  const response = await send(RECURRENCES_PATH, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  const body = parseSafely<LedgerRecurrencesEnvelope>(
    parseLedgerRecurrencesEnvelope,
    await readJson(response),
    response.status,
  );
  return { recurrences: body.data.recurrences, meta: body.meta };
}

/** `GET /api/v1/recurrences/{id}` — one schedule, with every generated occurrence classified. */
export async function readRecurrence(
  csrfToken: string | undefined,
  id: string,
  signal?: AbortSignal,
): Promise<RecurrenceDetailBody> {
  const response = await send(`${RECURRENCES_PATH}/${id}`, { method: 'GET', csrfToken, signal });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerRecurrenceEnvelope>(
    parseLedgerRecurrenceEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Store a `recurrence.create` or `recurrence.stop` preview.
 *
 * Both are review-required actions on the shared spine, so this is the same `POST
 * /mutation-previews` the other actions use — and the response is parsed by the recurrence
 * preview validator, whose `data` is the `oneOf` of the two: a create preview can never be
 * rendered as a stop preview.
 */
export async function createRecurrencePreview(
  csrfToken: string | undefined,
  body: RecurrenceMutationPreviewRequest,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<RecurrencePreviewBody> {
  const response = await send(PREVIEWS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerRecurrencePreviewEnvelope>(
    parseLedgerRecurrencePreviewEnvelope,
    await readJson(response),
    response.status,
  ).data;
}

/**
 * Commit a stored recurrence preview.
 *
 * `impactAcknowledged` is sent deliberately, `false` included: a creation whose dates already
 * carry entries is refused `409` `IMPACT_CONFIRMATION_REQUIRED` without it, and that refusal is
 * the protection. `write.ifMatch` carries the schedule's revision for a stop and is absent for a
 * creation, which has nothing to version — the API is the layer that refuses the wrong one.
 */
export async function commitRecurrence(
  csrfToken: string | undefined,
  previewId: string,
  impactAcknowledged: boolean,
  write: WriteHeaders,
  signal?: AbortSignal,
): Promise<RecurrenceTemplateBody> {
  const body: MutationCommitRequest = { previewId, confirm: true, impactAcknowledged };
  const response = await send(MUTATIONS_PATH, {
    method: 'POST',
    csrfToken,
    headers: writeHeaders(write),
    body,
    signal,
  });
  if (!response.ok) throw await failureOf(response);
  return parseSafely<LedgerRecurrenceTemplateEnvelope>(
    parseLedgerRecurrenceTemplateEnvelope,
    await readJson(response),
    response.status,
  ).data;
}
